import { BillingActivity } from "types/application";
import moment from "moment";
import logoSquare from "shared/branding/logo.svg";
import { formatToCurrency } from "shared/utils";
import { FormatAddress } from "./FormatAddress";
import { CaliforniaRows } from "./CaliforniaRows";
import { NonCaliforniaRows } from "./NonCaliforniaRows";
import { isTemplateFeeCategory } from "../utils";

const formatMoney = (cents: number) =>
  formatToCurrency({ cents, currency: "USD" }, { minimumFractionDigits: 2 });

const sumByCategory = (activities: BillingActivity[], category: string) =>
  activities
    .filter((a) => a.category === category)
    .reduce((sum, a) => sum + a.amount.cents, 0);

export const Receipt = ({
  billingActivities,
  primaryActivity
}: {
  billingActivities: BillingActivity[];
  primaryActivity: BillingActivity;
}) => {
  const isCaliforniaAddress = primaryActivity.metadata.one_line_address
    ? primaryActivity.metadata.one_line_address.toLowerCase().includes(" ca") ||
      primaryActivity.metadata.one_line_address
        .toLowerCase()
        .includes(" california")
    : false;

  const discountAmount = sumByCategory(billingActivities, "discount_fee");
  const applicationFeeAmount = sumByCategory(
    billingActivities,
    "applicant_application_fee"
  );
  const adminFeeAmount = sumByCategory(
    billingActivities,
    "applicant_administration_fee"
  );
  const holdingFeeAmount = sumByCategory(
    billingActivities,
    "applicant_holding_fee"
  );

  // Verification fees only (excluding application fee, discount, and template fees)
  const totalExpenses = billingActivities
    .filter(
      (item) =>
        item.amount.cents !== 0 &&
        item.category !== "applicant_application_fee" &&
        item.category !== "discount_fee" &&
        !isTemplateFeeCategory(item.category)
    )
    .reduce((sum, item) => sum + Math.abs(item.amount.cents), 0);

  const applicationFeeCollected =
    discountAmount !== 0
      ? applicationFeeAmount - Math.abs(discountAmount)
      : applicationFeeAmount;

  // Labor costs = Application cost - Total expenses
  const laborCostAmount = Math.max(0, applicationFeeAmount - totalExpenses);

  const hasTemplateFees = billingActivities.some((a) =>
    isTemplateFeeCategory(a.category)
  );

  const totalPaid = applicationFeeCollected + adminFeeAmount + holdingFeeAmount;

  const currentDate = moment().format("MMM DD, YYYY");

  return (
    <div>
      <div className="mx-auto px-12 py-8">
        {/* Logo Section */}
        <img
          src={logoSquare}
          alt="Boom Logo"
          className="h-6 w-auto mb-6"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />

        {/* Receipt Title */}
        <h1 className="text-center text-base font-bold text-content-1000 mb-4">
          Receipt
        </h1>

        {/* Header Information */}
        <div className="flex justify-between mb-8">
          <div className="flex-1">
            <p className="text-xs font-bold text-content-1000 mb-2">
              Boompay Inc.
            </p>
            <FormatAddress
              oneLineAddress={primaryActivity.metadata.one_line_address}
            />
          </div>

          <div className="flex-1 text-right text-xs">
            <HeaderField
              label="Receipt #"
              value={primaryActivity.id || "N/A"}
            />
            <HeaderField label="Receipt date" value={currentDate} />
            <HeaderField
              label="Applicant"
              value={primaryActivity.metadata.name || "N/A"}
            />
            <HeaderField
              label="Total paid"
              value={formatMoney(
                hasTemplateFees ? totalPaid : applicationFeeCollected
              )}
            />
            <HeaderField
              label="Paid date"
              value={
                primaryActivity.created_at
                  ? new Date(primaryActivity.created_at).toLocaleDateString(
                      "en-US",
                      { year: "numeric", month: "short", day: "2-digit" }
                    )
                  : currentDate
              }
            />
          </div>
        </div>

        {/* Expenses Table */}
        <div className="mb-4">
          <h2 className="text-xs font-bold text-content-1000 my-4">
            {isCaliforniaAddress ? "Screening expenses" : "Summary"}
          </h2>

          <table className="w-full text-xxs table-fixed border-b border-alpha-40">
            <thead className="border-b border-alpha-40">
              <tr className="text-right font-normal text-content-300">
                <th className="text-left pl-2">Item</th>
                <th>Quantity</th>
                <th>Item price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {isCaliforniaAddress ? (
                <CaliforniaRows billingActivities={billingActivities} />
              ) : (
                <NonCaliforniaRows
                  applicationFeeAmount={applicationFeeAmount}
                />
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-4">
            {isCaliforniaAddress && (
              <div className="space-y-2 mb-4">
                <SummaryLine
                  label="Total screening expenses"
                  cents={totalExpenses}
                />
                <SummaryLine label="Labor costs" cents={laborCostAmount} />
                <SummaryLine
                  label="Application cost"
                  cents={applicationFeeAmount}
                />
                <SummaryLine label="Applicant owes" cents={0} />
              </div>
            )}

            {discountAmount !== 0 && (
              <div className="mb-2">
                <SummaryLine
                  label="Discount"
                  cents={Math.abs(discountAmount)}
                  negative
                  bold
                />
              </div>
            )}

            <FeeCollectionSummary
              applicationFeeCollected={applicationFeeCollected}
              adminFeeAmount={adminFeeAmount}
              holdingFeeAmount={holdingFeeAmount}
              totalPaid={totalPaid}
              hasTemplateFees={hasTemplateFees}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FeeCollectionSummary = ({
  applicationFeeCollected,
  adminFeeAmount,
  holdingFeeAmount,
  totalPaid,
  hasTemplateFees
}: {
  applicationFeeCollected: number;
  adminFeeAmount: number;
  holdingFeeAmount: number;
  totalPaid: number;
  hasTemplateFees: boolean;
}) => {
  if (!hasTemplateFees)
    return <SummaryLine label="Total" cents={applicationFeeCollected} bold />;

  return (
    <>
      <div className="space-y-2 mb-4">
        <SummaryLine
          label="Application fee collected"
          cents={applicationFeeCollected}
          bold
        />
      </div>
      <div className="space-y-2 mb-4">
        {adminFeeAmount > 0 && (
          <SummaryLine
            label="Administration fee collected"
            cents={adminFeeAmount}
            bold
          />
        )}
        {holdingFeeAmount > 0 && (
          <SummaryLine
            label="Holding fee collected"
            cents={holdingFeeAmount}
            bold
          />
        )}
      </div>
      <SummaryLine label="Total paid" cents={totalPaid} bold large />
    </>
  );
};

const HeaderField = ({
  label,
  value
}: {
  label: string;
  value: string | number;
}) => (
  <p className="mb-1 flex justify-end text-xxs font-bold">
    <span className="text-content-1000 mr-2">{label}:</span>
    <span className="text-content-300">{value}</span>
  </p>
);

const SummaryLine = ({
  label,
  cents,
  negative,
  bold,
  large
}: {
  label: string;
  cents: number;
  negative?: boolean;
  bold?: boolean;
  large?: boolean;
}) => {
  const textSize = large ? "text-sm" : "text-xxs";
  const fontWeight = bold
    ? "font-bold text-content-1000"
    : "font-medium text-content-400";

  return (
    <p className={`flex justify-end items-center ${textSize} ${fontWeight}`}>
      <span className="mr-4">{label}</span>
      <span className="w-20 text-right">
        {negative && "-"}
        {formatMoney(cents)}
      </span>
    </p>
  );
};
